import { Text } from '../../cdl/Text/Text';
import { formatNumber } from '../../common/helpers/formatNumber.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Box } from '../../common/ui/Box';
import type { DemandProductCategoryOption } from '../types/DemandProductCategoryOption';
import type { DemandVesselBreakdown } from '../types/DemandVesselBreakdown';

import { DemandDelta } from './DemandDelta';
import { DemandLiftingCount } from './DemandLiftingCount';
import { DemandVolume } from './DemandVolume';
import { ResultingDemandCollapsable } from './ResultingDemandCollapsable';
import { ResultingDemandProductRow } from './ResultingDemandProductRow';
import { ResultingDemandRow } from './ResultingDemandRow';

interface ResultingDemandDrawerProps {
    vesselName: string;
    selectedOption?: string;
    historicalVolume: number;
    showChanges: boolean;
    demand?: DemandVesselBreakdown;
    categories: DemandProductCategoryOption[];
}

export const ResultingDemandDrawer = ({
    vesselName,
    selectedOption,
    historicalVolume,
    demand,
    categories,
    showChanges,
}: ResultingDemandDrawerProps) => {
    const categoryLabels = new Map(categories.map((category) => [category.id, category.label]));
    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box padding={24} overflow="auto" display="flex" flexDirection="column" gap={16} flex={1} minHeight={0}>
                <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={16} paddingX={8}>
                    <Text variant="small" fontWeight={600} color="foreground.default">
                        {vesselName}
                    </Text>
                    {selectedOption ? (
                        <Text variant="small" color="foreground.subtle" textAlign="right">
                            {selectedOption}
                        </Text>
                    ) : null}
                </Box>
                {!demand ? (
                    <Text variant="small">
                        {translate('demandDefinition.excludedFromDemandRecorded', {
                            volume: formatNumber({ number: historicalVolume, maximumFractionDigits: 0 }),
                        })}
                    </Text>
                ) : (
                    <>
                        <Box backgroundColor="background.subtle" borderRadius={8} paddingY={8}>
                            <ResultingDemandRow $showChanges={showChanges}>
                                <Text variant="fieldLabel" color="foreground.muted" paddingLeft={24}>
                                    {translate('demandDefinition.productCategory')}
                                </Text>
                                {showChanges ? (
                                    <>
                                        <Text variant="fieldLabel" color="foreground.muted">
                                            {translate('demandDefinition.historical')}
                                        </Text>
                                        <Text variant="fieldLabel" color="foreground.muted">
                                            {translate('demandDefinition.delta')}
                                        </Text>
                                    </>
                                ) : null}
                                <Text variant="fieldLabel" color="foreground.muted">
                                    {translate('demandDefinition.volume')}
                                </Text>
                            </ResultingDemandRow>
                        </Box>
                        <Box display="flex" flexDirection="column" gap={0}>
                            {demand.categories.map((category) => (
                                <ResultingDemandCollapsable
                                    key={category.category}
                                    title={categoryLabels.get(category.category) ?? category.category}
                                    summary={category.summary}
                                    liftingCount={category.liftingCount}
                                    showChanges={showChanges}
                                >
                                    {category.equivalenceGroups.map((group) => (
                                        <ResultingDemandCollapsable
                                            key={`${group.productKey.type}-${group.productKey.value}`}
                                            title={group.name}
                                            summary={group.summary}
                                            liftingCount={group.liftingCount}
                                            showChanges={showChanges}
                                            muted
                                        >
                                            {group.products.map((product) => (
                                                <ResultingDemandProductRow
                                                    key={product.productId}
                                                    product={product}
                                                    showChanges={showChanges}
                                                />
                                            ))}
                                        </ResultingDemandCollapsable>
                                    ))}
                                </ResultingDemandCollapsable>
                            ))}
                        </Box>
                        <Box backgroundColor="background.subtle" borderRadius={8} paddingY={8}>
                            <ResultingDemandRow $showChanges={showChanges}>
                                <Box display="flex" flexDirection="column" paddingLeft={24}>
                                    <Text variant="small" fontWeight={600}>
                                        {translate('global.total')}
                                    </Text>
                                    <DemandLiftingCount count={demand.liftingCount} />
                                </Box>
                                {showChanges ? (
                                    <>
                                        <Text variant="small" color="foreground.subtle">
                                            <DemandVolume volume={demand.summary.historicalVolume} />
                                        </Text>
                                        <DemandDelta
                                            deltaVolume={demand.summary.deltaVolume}
                                            deltaPercent={demand.summary.deltaPercent}
                                        />
                                    </>
                                ) : null}
                                <Text variant="small" fontWeight={600}>
                                    <DemandVolume volume={demand.summary.adjustedVolume} />
                                </Text>
                            </ResultingDemandRow>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};
